import React, { useEffect, useMemo, useState } from "react";
import { X, Check, Undo2, ChevronDown, ChevronRight } from "lucide-react";
import * as XLSX from "xlsx";

// ===== helpers =====
const deepClone = (obj) => JSON.parse(JSON.stringify(obj || {}));

// ===================================================
// 🧮 Enhanced Stock Simulation with Dynamic Amazon Splits - FIXED VERSION
// ===================================================

// Helper function for getting Amazon share



function getAmazonShare(percentages, region, product, size) {
  if (!percentages?.amazon_share) return 0.25;

  const split = percentages.amazon_share.split;
  if (!split || !percentages.amazon_share.showSplit) {
    return percentages.amazon_share.total || 0.25;
  }

  // Try to get specific split
  try {
    const productKey = product === "20" ? "2_0" : product;
    if (split[region]?.[productKey]?.[size]) {
      return split[region][productKey][size] / 100;
    }
    // Fallback to region total
    if (split[region]?.total) {
      return split[region].total;
    }
  } catch (e) {
    console.warn("Error getting Amazon share:", e);
  }

  // Final fallback
  return percentages.amazon_share.total || 0.25;
}

export function simulatePlanningTableStock(allData, baseDayData, percentages, manualStockOverrides) {
  if (!Array.isArray(allData) || !baseDayData?.detailed || !percentages) return [];

  const sorted = [...allData].sort((a, b) => new Date(a.date) - new Date(b.date));
  const todayISO = new Date().toISOString().split("T")[0];

  const salesSplit = percentages.sales || {};
  const prodSplit = percentages.production || {};
  const mlSalesSplit = percentages.ml_sales || {};
  const mlProdSplit = percentages.ml_production || {};

  let running = {
    us_cityplay_m: baseDayData.detailed.stock_us_cityplay_m || 0,
    us_cityplay_l: baseDayData.detailed.stock_us_cityplay_l || 0,
    us_20_m: baseDayData.detailed.stock_us_20_m || 0,
    us_20_l: baseDayData.detailed.stock_us_20_l || 0,
    uk_cityplay_m: baseDayData.detailed.stock_uk_cityplay_m || 0,
    uk_cityplay_l: baseDayData.detailed.stock_uk_cityplay_l || 0,
    uk_20_m: baseDayData.detailed.stock_uk_20_m || 0,
    uk_20_l: baseDayData.detailed.stock_uk_20_l || 0,
  };

  let runningAmazon = {
    us_cityplay_m: baseDayData.detailed.stock_amazon_us_cityplay_m || 0,
    us_cityplay_l: baseDayData.detailed.stock_amazon_us_cityplay_l || 0,
    us_20_m: baseDayData.detailed.stock_amazon_us_20_m || 0,
    us_20_l: baseDayData.detailed.stock_amazon_us_20_l || 0,
    uk_cityplay_m: baseDayData.detailed.stock_amazon_uk_cityplay_m || 0,
    uk_cityplay_l: baseDayData.detailed.stock_amazon_uk_cityplay_l || 0,
    uk_20_m: baseDayData.detailed.stock_amazon_uk_20_m || 0,
    uk_20_l: baseDayData.detailed.stock_amazon_uk_20_l || 0,
  };

  const amazonObj = percentages.amazon_share || {};
  const AMAZON_TOTAL = amazonObj.total ?? 0.25;
  const amazonSplit = amazonObj.split || {};

  function getAmazonSplit(region, product, size) {
    try {
      const regionData = amazonSplit?.[region];
      if (!regionData) return AMAZON_TOTAL;
      const base = regionData[product]?.[size] ?? 0;
      const regionWeight = regionData.total ?? 1;
      return (AMAZON_TOTAL * regionWeight * base) / 100;
    } catch {
      return AMAZON_TOTAL;
    }
  }

  const pendingArrivals = {};

  const simulated = sorted.map((day) => {
    const isFuture = day.date > todayISO;
    const isToday = day.date === todayISO;
    if (!isFuture && !isToday) return day;

    const totalProd = day.forecast?.production_total || day.manufacturing?.plan_target_total || 0;
    const totalSales = day.sales?.target_total || 0;

    const prodGroups = {
      us_cityplay: (totalProd * (prodSplit.us_cityplay || 0)) / 100,
      us_20: (totalProd * (prodSplit.us_20 || 0)) / 100,
      uk_cityplay: (totalProd * (prodSplit.uk_cityplay || 0)) / 100,
      uk_20: (totalProd * (prodSplit.uk_20 || 0)) / 100,
    };

    const prodML = {
      us_cityplay_m: Math.round((prodGroups.us_cityplay * (mlProdSplit.us_cityplay?.m || 50)) / 100),
      us_cityplay_l: Math.round((prodGroups.us_cityplay * (mlProdSplit.us_cityplay?.l || 50)) / 100),
      us_20_m: Math.round((prodGroups.us_20 * (mlProdSplit.us_20?.m || 50)) / 100),
      us_20_l: Math.round((prodGroups.us_20 * (mlProdSplit.us_20?.l || 50)) / 100),
      uk_cityplay_m: Math.round((prodGroups.uk_cityplay * (mlProdSplit.uk_cityplay?.m || 50)) / 100),
      uk_cityplay_l: Math.round((prodGroups.uk_cityplay * (mlProdSplit.uk_cityplay?.l || 50)) / 100),
      uk_20_m: Math.round((prodGroups.uk_20 * (mlProdSplit.uk_20?.m || 50)) / 100),
      uk_20_l: Math.round((prodGroups.uk_20 * (mlProdSplit.uk_20?.l || 50)) / 100),
    };

    const salesGroups = {
      us_cityplay: (totalSales * (salesSplit.us_cityplay || 0)) / 100,
      us_20: (totalSales * (salesSplit.us_20 || 0)) / 100,
      uk_cityplay: (totalSales * (salesSplit.uk_cityplay || 0)) / 100,
      uk_20: (totalSales * (salesSplit.uk_20 || 0)) / 100,
    };

    const salesML = {
      us_cityplay_m: Math.round((salesGroups.us_cityplay * (mlSalesSplit.us_cityplay?.m || 50)) / 100),
      us_cityplay_l: Math.round((salesGroups.us_cityplay * (mlSalesSplit.us_cityplay?.l || 50)) / 100),
      us_20_m: Math.round((salesGroups.us_20 * (mlSalesSplit.us_20?.m || 50)) / 100),
      us_20_l: Math.round((salesGroups.us_20 * (mlSalesSplit.us_20?.l || 50)) / 100),
      uk_cityplay_m: Math.round((salesGroups.uk_cityplay * (mlSalesSplit.uk_cityplay?.m || 50)) / 100),
      uk_cityplay_l: Math.round((salesGroups.uk_cityplay * (mlSalesSplit.uk_cityplay?.l || 50)) / 100),
      uk_20_m: Math.round((salesGroups.uk_20 * (mlSalesSplit.uk_20?.m || 50)) / 100),
      uk_20_l: Math.round((salesGroups.uk_20 * (mlSalesSplit.uk_20?.l || 50)) / 100),
    };

// =====================================
// 🧮 Accurate Regular vs Amazon Split (matches PlanningTable.js)
// =====================================
const regularSales = {};
const amazonSales = {};

const amazonObj = percentages.amazon_share || {};
const AMAZON_TOTAL = amazonObj.total ?? 0.25; // 25%
const amazonSplit = amazonObj.split || {};

// Compute Amazon share for each SKU
function getAmazonShare(region, product, size) {
  try {
    const regionData = amazonSplit?.[region];
    if (!regionData) return 0;
    const base = regionData[product]?.[size] ?? 0;    // e.g. 0.33, 0.08...
    const regionWeight = regionData.total ?? 0;       // e.g. us.total = 0.75
    // Includes both 25% * region share * base * (already includes 90% utilization)
    return AMAZON_TOTAL * regionWeight * base;
  } catch {
    return 0;
  }
}

// 75% regular warehouses, 25% Amazon (but effectively 22.5%)
const REGULAR_FACTOR = 1 - AMAZON_TOTAL;

Object.keys(salesML).forEach((k) => {
  const [region, rawProduct, size] = k.split("_");
  const product = rawProduct === "20" ? "2_0" : rawProduct;
  const amazonShare = getAmazonShare(region, product, size);

  // Amazon and regular split aligned with PlanningTable logic
  amazonSales[k] = Math.round(totalSales * amazonShare);
  regularSales[k] = Math.round(salesML[k] * REGULAR_FACTOR);
});



    const nextRunning = { ...running };
    const nextAmazon = { ...runningAmazon };

    Object.keys(salesML).forEach((k) => {
      nextRunning[k] = (nextRunning[k] || 0) - (regularSales[k] || 0);
      nextAmazon[k] = (nextAmazon[k] || 0) - (amazonSales[k] || 0);
    });

    if (pendingArrivals[day.date]) {
      Object.entries(pendingArrivals[day.date]).forEach(([sku, qty]) => {
        if (sku.startsWith("amazon_")) {
          const clean = sku.replace(/^amazon_/, "");
          nextAmazon[clean] = (nextAmazon[clean] || 0) + qty;
        } else {
          nextRunning[sku] = (nextRunning[sku] || 0) + qty;
        }
      });
    }

    Object.keys(prodML).forEach((k) => {
      const [region, rawProduct, size] = k.split("_");
      const product = rawProduct === "20" ? "2_0" : rawProduct;
      const baseSplit = getAmazonSplit(region, product, size);
      const effectiveShare = baseSplit > 0 ? baseSplit : AMAZON_TOTAL;
      const addedRegular = Math.round(prodML[k] * (1 - effectiveShare));
      const addedAmazon = Math.round(prodML[k] * effectiveShare);
      const isUK = region === "uk";
      const transitDays = isUK ? 7 : 6;
      const arrival = new Date(day.date);
      arrival.setDate(arrival.getDate() + transitDays);
      const arrivalISO = arrival.toISOString().split("T")[0];
      if (!pendingArrivals[arrivalISO]) pendingArrivals[arrivalISO] = {};
      pendingArrivals[arrivalISO][k] = (pendingArrivals[arrivalISO][k] || 0) + addedRegular;
      pendingArrivals[arrivalISO][`amazon_${k}`] = (pendingArrivals[arrivalISO][`amazon_${k}`] || 0) + addedAmazon;
    });

    const manual = manualStockOverrides?.[day.date];
    if (manual) {
      Object.entries(manual).forEach(([key, val]) => {
        if (typeof val !== "number") return;
        if (key.startsWith("us_amazon_") || key.startsWith("uk_amazon_") || key.startsWith("amazon_")) {
          const cleanKey = key.replace(/^amazon_/, "").replace(/^us_amazon_/, "us_").replace(/^uk_amazon_/, "uk_");
          nextAmazon[cleanKey] = (nextAmazon[cleanKey] || 0) + val;
        } else {
          nextRunning[key] = (nextRunning[key] || 0) + val;
        }
      });
    }

    const total_stock =
      Object.values(nextRunning).reduce((a, b) => a + b, 0) +
      Object.values(nextAmazon).reduce((a, b) => a + b, 0);

    running = { ...nextRunning };
    runningAmazon = { ...nextAmazon };

    return {
      ...day,
      stock: {
        ...nextRunning,
        amazon: nextAmazon,
        total_stock: Math.round(total_stock),
      },
      forecast: {
        ...day.forecast,
        sales: regularSales,
        sales_amazon: amazonSales,
      },
    };
  });

  return simulated;
}



// ===================================================
// 🎯 Apply Sales Target with Dynamic Amazon Split
// ===================================================
function applySalesTargetWithPercentsPerItem(day, newTarget, percentages) {
  if (!percentages) return;

  const db = percentages;

  // Calculate base percentages for each SKU
  const basePercents = {};
  const skus = ['us_cityplay_m', 'us_cityplay_l', 'us_20_m', 'us_20_l',
               'uk_cityplay_m', 'uk_cityplay_l', 'uk_20_m', 'uk_20_l'];

  skus.forEach(sku => {
    const [region, product, size] = sku.split('_');
    const groupPercent = db.sales[`${region}_${product}`] || 0;
    const sizePercent = db.ml_sales[`${region}_${product}`]?.[size] || 50;
    basePercents[sku] = (groupPercent * sizePercent) / 100;
  });

  // Normalize and distribute
  const totalP = Object.values(basePercents).reduce((a, b) => a + b, 0) || 1;
  const factor = newTarget / totalP;

  const salesRegular = {};
  const salesAmazon = {};

  Object.entries(basePercents).forEach(([sku, percent]) => {
    const total = Math.round(percent * factor);
    const [region, product, size] = sku.split('_');
    const amazonShare = getAmazonShare(percentages, region, product, size);

    salesAmazon[sku] = Math.round(total * amazonShare);
    salesRegular[sku] = total - salesAmazon[sku];
  });

  // Save into the day
  day.forecast = day.forecast || {};
  day.forecast.sales = salesRegular;
  day.forecast.sales_amazon = salesAmazon;
  day.sales = { ...(day.sales || {}), target_total: newTarget };
}

// ===================================================
// 🏭 Apply Production Target with Dynamic Amazon Split
// ===================================================
function applyProductionTargetWithPercentsPerItem(day, newTarget, percentages) {
  if (!percentages) return;

  const db = percentages;

  // Calculate base percentages for each SKU
  const basePercents = {};
  const skus = ['us_cityplay_m', 'us_cityplay_l', 'us_20_m', 'us_20_l',
               'uk_cityplay_m', 'uk_cityplay_l', 'uk_20_m', 'uk_20_l'];

  skus.forEach(sku => {
    const [region, product, size] = sku.split('_');
    const groupPercent = db.production[`${region}_${product}`] || 0;
    const sizePercent = db.ml_production[`${region}_${product}`]?.[size] || 50;
    basePercents[sku] = (groupPercent * sizePercent) / 100;
  });

  // Normalize and distribute
  const totalP = Object.values(basePercents).reduce((a, b) => a + b, 0) || 1;
  const factor = newTarget / totalP;

  const prodRegular = {};
  const prodAmazon = {};

  Object.entries(basePercents).forEach(([sku, percent]) => {
    const total = Math.round(percent * factor);
    const [region, product, size] = sku.split('_');
    const amazonShare = getAmazonShare(percentages, region, product, size);

    prodAmazon[sku] = Math.round(total * amazonShare);
    prodRegular[sku] = total - prodAmazon[sku];
  });

  // Save into the day
  day.forecast = day.forecast || {};
  day.forecast.production = prodRegular;
  day.forecast.production_amazon = prodAmazon;
  day.production_target_total = newTarget;
}

// ===================================================
// Main Component
// ===================================================
export default function SimulationBoard({
  open,
  onClose,
  data,
  onApplyToDb,
  manualStockOverrides,
  setManualStockOverrides
}) {
  const [original, setOriginal] = useState([]);
  const [rows, setRows] = useState([]);
  const [baseDay, setBaseDay] = useState(null);
  const [percentages, setPercentages] = useState(null);
  const [manualInputs, setManualInputs] = useState({});
  const [isLoadingVersion, setIsLoadingVersion] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [lockedCells, setLockedCells] = useState({});
  const [collapsedSections, setCollapsedSections] = useState({
    sales: false,
    production: false,
    stock: false
  });

useEffect(() => {
  if (isLoadingVersion) {
    setIsLoadingVersion(false);
    return;
  }

  if (!Array.isArray(rows) || !rows.length) return;

  setManualStockOverrides((prev) => {
    if (Object.keys(prev || {}).length > 0) return prev;

    const autoOverrides = {};
    rows.forEach((row) => {
      const dayProd = row.forecast?.production || {};
      const dayProdAmazon = row.forecast?.production_amazon || {};
      const allProd = { ...dayProd, ...dayProdAmazon };

      Object.entries(allProd).forEach(([field, qty]) => {
        if (qty === null || qty === undefined || isNaN(qty)) return;
        const isUK = field.startsWith("uk_");
        const isAmazon = field.startsWith("amazon_") || field.includes("amazon_");
        const transitDays = isUK ? 7 : 6;
        const arrivalDate = new Date(row.date);
        arrivalDate.setDate(arrivalDate.getDate() + transitDays);
        const arrivalISO = arrivalDate.toISOString().split("T")[0];
        const skuKey = isAmazon ? field : field.replace(/^eu_/, "uk_");
        if (!autoOverrides[arrivalISO]) autoOverrides[arrivalISO] = {};

        if (qty === 0) {
          delete autoOverrides[arrivalISO][skuKey];
        } else {
          autoOverrides[arrivalISO][skuKey] = qty;
        }
      });
    });

    return autoOverrides;
  });
}, [rows, isLoadingVersion]);





  // === Helper function to get display value with override support ===
const getDisplayValue = (day, field, isAmazon = false) => {
  const arrivalDate = new Date(day.date);
  const isUK = field.startsWith("uk_");
  const transitDays = isUK ? 7 : 6;
  arrivalDate.setDate(arrivalDate.getDate() + transitDays);
  const arrivalISO = arrivalDate.toISOString().split("T")[0];

  if (manualStockOverrides?.[arrivalISO]) {
    const regionPrefix = field.startsWith("uk_") ? "uk" : "us";
    const cleanField = field.replace(/^uk_/, "").replace(/^us_/, "");
    const key = isAmazon
      ? `${regionPrefix}_amazon_${cleanField}`
      : `${regionPrefix}_${cleanField}`;
    const val = manualStockOverrides[arrivalISO][key];
    if (val !== undefined && val !== null) return val;
  }

  if (!day?.forecast?.production) return 0;
  if (isAmazon) {
    return day.forecast.production[`amazon_${field}`] ?? 0;
  } else {
    return day.forecast.production[field] ?? 0;
  }
};


  // === INIT ===
  useEffect(() => {
    const initSimulation = async () => {
      try {


        // Fetch percentages
        const percRes = await fetch("/api/plan/percentages");
        const percData = percRes.ok ? await percRes.json() : null;


        if (percData?.success) {
          setPercentages(percData);
          console.log("✅ Set percentages with success flag");
        } else {
          setPercentages(percData || {});
          console.log("⚠️ Set percentages without success flag:", percData);
        }

        // Fetch yesterday (base day)
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        const yStr = yesterday.toISOString().split("T")[0];
        const res = await fetch(`/api/plan/day/${yStr}`);
        const baseDay = res.ok ? await res.json() : null;
        if (baseDay) setBaseDay(baseDay);


        // Load planning data
        let base = deepClone(data || []);
        if (baseDay && percData) {
          base = simulatePlanningTableStock(base, baseDay, percData, manualStockOverrides || {});
        }

        // ✅ show all dates including past
        setOriginal(base);
        setRows(base);

      } catch (err) {
        console.error("❌ initSimulation failed:", err);
      }
    };

    if (open) initSimulation();
  }, [open, data]);



// === Compute depletion info per SKU (Regular vs Amazon) ===
const depletionInfo = useMemo(() => {
  if (!rows?.length) return { regular: {}, amazon: {} };

  const result = { regular: {}, amazon: {} };
  const skuList = [
    "us_cityplay_m", "us_cityplay_l", "us_20_m", "us_20_l",
    "uk_cityplay_m", "uk_cityplay_l", "uk_20_m", "uk_20_l"
  ];

  skuList.forEach((sku) => {
    const days = rows.filter(d => d.date && d.stock);
    if (!days.length) return;

    const findOutLogic = (getter) => {
      let lastMinusDay = null;
      let currentlyNegative = false;

      for (let i = 0; i < days.length; i++) {
        const currQty = getter(days[i]) ?? 0;
        if (currQty < 0 && !currentlyNegative) {
          lastMinusDay = days[i];
          currentlyNegative = true;
        } else if (currQty >= 0 && currentlyNegative) {
          currentlyNegative = false;
        }
      }

      const lastQty = getter(days.at(-1)) ?? 0;
      if (lastMinusDay && lastQty < 0) {
        return { outDate: lastMinusDay.date, current: lastQty };
      } else {
        return { outDate: null, current: lastQty };
      }
    };

    const regular = findOutLogic((d) => d.stock?.[sku]);
    const amazon = findOutLogic((d) => d.stock?.amazon?.[sku]);

    result.regular[sku] = regular;
    result.amazon[sku] = amazon;
  });

  return result;
}, [rows]);


  // Detect unsaved changes
  const isDirty = useMemo(
    () => JSON.stringify(rows) !== JSON.stringify(original),
    [rows, original]
  );

  if (!open) return null;

  // === Handlers ===
  const handleChangeTarget = (idx, value, type) => {
    const target = Math.max(0, parseInt(value || 0, 10) || 0);
    const next = deepClone(rows);

    if (type === 'sales') {
      applySalesTargetWithPercentsPerItem(next[idx], target, percentages);
    } else {
      applyProductionTargetWithPercentsPerItem(next[idx], target, percentages);
    }

    const updated = simulatePlanningTableStock(next, baseDay, percentages, manualStockOverrides);
    setRows(updated);
  };

  const handleChangeSalesField = (idx, field, value, isAmazon = false) => {
    const qty = Math.max(0, parseInt(value || 0, 10) || 0);
    const next = deepClone(rows);
    next[idx].forecast = next[idx].forecast || {};

    if (isAmazon) {
      next[idx].forecast.sales_amazon = next[idx].forecast.sales_amazon || {};
      next[idx].forecast.sales_amazon[field] = qty;
    } else {
      next[idx].forecast.sales = next[idx].forecast.sales || {};
      next[idx].forecast.sales[field] = qty;
    }

    // Update target_total
    const regularSum = Object.values(next[idx].forecast.sales || {}).reduce((a, b) => a + (b || 0), 0);
    const amazonSum = Object.values(next[idx].forecast.sales_amazon || {}).reduce((a, b) => a + (b || 0), 0);
    next[idx].sales = { ...(next[idx].sales || {}), target_total: regularSum + amazonSum };

    const updated = simulatePlanningTableStock(next, baseDay, percentages, manualStockOverrides);
    setRows(updated);
  };



const handleChangeProdField = (idx, field, value, isAmazon = false) => {
  const parsed = parseInt(value, 10);
  const qty = isNaN(parsed) ? 0 : Math.max(0, parsed);
  const next = deepClone(rows);
  next[idx].forecast = next[idx].forecast || {};
  next[idx].forecast.production = next[idx].forecast.production || {};

  const isUK = field.startsWith("uk_");
  const region = isUK ? "uk" : "us";
  const clean = field.replace(/^uk_/, "").replace(/^us_/, "");
  const key = isAmazon ? `amazon_${region}_${clean}` : `${region}_${clean}`;

  if (qty === 0) {
    delete next[idx].forecast.production[key];
  } else {
    next[idx].forecast.production[key] = qty;
  }

  const prodSum = Object.values(next[idx].forecast.production || {}).reduce((a, b) => a + (b || 0), 0);
  next[idx].production_target_total = prodSum;

  const updatedOverrides = deepClone(manualStockOverrides);
  const transitDays = isUK ? 7 : 6;
  const arrival = new Date(next[idx].date);
  arrival.setDate(arrival.getDate() + transitDays);
  const arrivalISO = arrival.toISOString().split("T")[0];

  if (!updatedOverrides[arrivalISO]) updatedOverrides[arrivalISO] = {};

  if (qty === 0) {
    delete updatedOverrides[arrivalISO][key];
    if (Object.keys(updatedOverrides[arrivalISO]).length === 0) {
      delete updatedOverrides[arrivalISO];
    }
  } else {
    updatedOverrides[arrivalISO][key] = qty;
  }

  setManualStockOverrides(updatedOverrides);
  const updated = simulatePlanningTableStock(next, baseDay, percentages, updatedOverrides);
  setRows(updated);
};



  const reset = () => {
    setRows(deepClone(original));
  };

  const applyToDb = async () => {
    if (!onApplyToDb) return;

    const payload = rows.map((d) => ({
      date: d.date,
      forecast: {
        sales: d.forecast?.sales || {},
        sales_amazon: d.forecast?.sales_amazon || {},
        production: d.forecast?.production || {},
        production_amazon: d.forecast?.production_amazon || {},
        shipping: {
          us_days: d.shipment?.duration_days?.china_us || 20,
          eu_days: d.shipment?.duration_days?.china_uk || 15,
          us_customer: d.shipment?.duration_days?.us_customer || 7,
          uk_customer: d.shipment?.duration_days?.uk_customer || 5,
        },
      },
    }));

    await onApplyToDb(payload);
  };

const handleSaveVersion = async () => {
  try {
    const name = prompt("Enter a name for this version:");
    if (!name) return;

    const cleanedRows = deepClone(rows);
    cleanedRows.forEach((r) => {
      delete r.stock;
    });

    const payload = {
      rows: cleanedRows,
      percentages,
      baseDay,
      savedAt: new Date().toISOString(),
    };

    const res = await fetch(`${process.env.REACT_APP_API_URL}/api/plan/version/save/${encodeURIComponent(name)}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    const data = await res.json();
    if (data.success) {
      alert(`✅ Saved version '${name}' successfully!`);
    } else {
      alert(`❌ Failed to save version: ${data.error || "Unknown error"}`);
    }
  } catch (err) {
    console.error("❌ handleSaveVersion error:", err);
    alert("Error saving version");
  }
};


const normalizeOverrideKeys = (src = {}) => {
  const out = {};
  Object.entries(src).forEach(([date, obj]) => {
    const row = {};
    Object.entries(obj || {}).forEach(([k, v]) => {
      let key = k;

      if (key.startsWith("amazon_us_")) key = key.replace(/^amazon_us_/, "us_amazon_");
      else if (key.startsWith("amazon_uk_")) key = key.replace(/^amazon_uk_/, "uk_amazon_");
      else if (key.startsWith("amazon_")) {
        const parts = key.split("_");
        if (parts.length >= 3) {
          const region = parts[1];
          key = `${region}_amazon_${parts.slice(2).join("_")}`;
        }
      }

      if (key.startsWith("eu_")) key = key.replace(/^eu_/, "uk_");
      if (key.startsWith("amazon_eu_")) key = key.replace(/^amazon_eu_/, "uk_amazon_");

      row[key] = v;
    });
    out[date] = row;
  });
  return out;
};

const handleLoadVersion = async () => {
  try {
    const res = await fetch(`${process.env.REACT_APP_API_URL}/api/plan/version/list`);
    const data = await res.json();
    if (!data.success || !data.versions?.length) return alert("No saved versions found");

    const versionLines = data.versions.map(
      (v, i) => `${i + 1}. ${v.name} (${new Date(v.modified).toLocaleString()})`
    );
    const choice = prompt("Choose version number to load:\n" + versionLines.join("\n"));
    const index = parseInt(choice) - 1;
    if (isNaN(index) || index < 0 || index >= data.versions.length) return;

    const selected = data.versions[index].name;
    const loadRes = await fetch(`${process.env.REACT_APP_API_URL}/api/plan/version/load/${selected}`);
    const loaded = await loadRes.json();
    if (!loaded || !loaded.rows) return alert("Invalid version file");

    const normalizedRows = deepClone(loaded.rows || []).map((r) => ({
      ...r,
      forecast: {
        ...r.forecast,
        production: r.forecast?.production || {},
        production_amazon: r.forecast?.production_amazon || {},
        sales: r.forecast?.sales || {},
      },
      sales: r.sales || {},
      manufacturing: r.manufacturing || {},
      stock: null,
    }));

    const originalOverrides = normalizeOverrideKeys(manualStockOverrides || {});
    const updatedOverrides = deepClone(originalOverrides);

    normalizedRows.forEach((day) => {
      const prod = day.forecast?.production || {};
      Object.entries(prod).forEach(([field, qty]) => {
        if (!qty || qty <= 0) return;
        const region = field.startsWith("uk_") ? "uk" : "us";
        const clean = field.replace(/^uk_/, "").replace(/^us_/, "");
        const key = `${region}_${clean}`;
        const arrival = new Date(day.date);
        arrival.setDate(arrival.getDate() + (region === "uk" ? 7 : 6));
        const arrivalISO = arrival.toISOString().split("T")[0];
        if (!updatedOverrides[arrivalISO]) updatedOverrides[arrivalISO] = {};
        if (updatedOverrides[arrivalISO][key] == null) {
          updatedOverrides[arrivalISO][key] = qty;
        }
      });

      const prodAmz = day.forecast?.production_amazon || {};
      Object.entries(prodAmz).forEach(([field, qty]) => {
        if (!qty || qty <= 0) return;
        const region = field.startsWith("uk_") ? "uk" : "us";
        const clean = field.replace(/^uk_/, "").replace(/^us_/, "");
        const key = `${region}_amazon_${clean}`;
        const arrival = new Date(day.date);
        arrival.setDate(arrival.getDate() + (region === "uk" ? 7 : 6));
        const arrivalISO = arrival.toISOString().split("T")[0];
        if (!updatedOverrides[arrivalISO]) updatedOverrides[arrivalISO] = {};
        if (updatedOverrides[arrivalISO][key] == null) {
          updatedOverrides[arrivalISO][key] = qty;
        }
      });
    });

    const recalculated = simulatePlanningTableStock(
      normalizedRows,
      loaded.baseDay || null,
      loaded.percentages || {},
      updatedOverrides
    );

    setRows(recalculated);
    setManualStockOverrides(updatedOverrides);
    setPercentages(loaded.percentages || {});
    setBaseDay(loaded.baseDay || null);
    setRefreshKey(Date.now());

    const newLocked = {};
    Object.entries(originalOverrides).forEach(([arrivalDate, cells]) => {
      Object.keys(cells || {}).forEach((skuKey) => {
        const isUK = skuKey.startsWith("uk_");
        const daysToSubtract = isUK ? 7 : 6;
        const arrival = new Date(arrivalDate);
        arrival.setDate(arrival.getDate() - daysToSubtract);
        const prodDate = arrival.toISOString().split("T")[0];
        if (!newLocked[prodDate]) newLocked[prodDate] = {};

        newLocked[prodDate][skuKey] = true;

        if (skuKey.includes("_amazon_")) {
          const alias = skuKey
            .replace("_amazon_", "_")
            .concat("__amz");
          newLocked[prodDate][alias] = true;
        }
      });
    });

    setLockedCells(newLocked);

    alert(`✅ Loaded version '${selected}' successfully!`);
  } catch (err) {
    console.error("❌ handleLoadVersion error:", err);
    alert("Error loading version");
  }
};



  const handleApplyManualOverrides = async () => {
    console.log("🚀 Starting manual override apply with dynamic Amazon splits");

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/manual-overrides`);
      const existingJson = res.ok ? await res.json() : {};
      const merged = deepClone(existingJson);

      Object.entries(manualInputs).forEach(([date, additions]) => {
        Object.entries(additions).forEach(([fullSku, val]) => {
          if (!val || Number(val) === 0) return;

          // Parse SKU and check if it's Amazon
          const isAmazon = fullSku.startsWith('amazon_');
          const sku = isAmazon ? fullSku.replace('amazon_', '') : fullSku;
          const normalizedSku = sku.replace(/^eu_/, "uk_");

          // Determine arrival date
          const isUK = normalizedSku.startsWith("uk_");
          const daysToArrival = isUK ? 7 : 6;
          const arrival = new Date(date);
          arrival.setDate(arrival.getDate() + daysToArrival);
          const arrivalISO = arrival.toISOString().split("T")[0];

          // Store with proper Amazon prefix if needed
          const finalSku = isAmazon ? `amazon_${normalizedSku}` : normalizedSku;

          if (!merged[arrivalISO]) merged[arrivalISO] = {};
          merged[arrivalISO][finalSku] = (merged[arrivalISO][finalSku] || 0) + Number(val);


        });
      });


      await fetch(`${process.env.REACT_APP_API_URL}/api/manual-overrides`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(merged),
      });

      alert("Manual overrides saved with dynamic Amazon splits!");
    } catch (err) {
      console.error("❌ handleApplyManualOverrides failed:", err);
    }
  };

  // === Styles ===
  const tableContainer = {
    position: "relative",
    maxHeight: "85vh",
    overflow: "auto",
    background: "white",
  };

  const thSticky = {
    position: "sticky",
    top: 0,
    background: "#f8fafc",
    border: "1px solid #e5e7eb",
    padding: "0.4rem",
    fontSize: "0.72rem",
    whiteSpace: "nowrap",
  };

  const colBg = (bg) => ({
    background: bg,
    border: "1px solid #e5e7eb",
    padding: "0.3rem",
    textAlign: "center",
    whiteSpace: "nowrap",
  });

  const inputCell = {
    width: "3.5rem",
    fontSize: "0.7rem",
    padding: "0.2rem 0.35rem",
    textAlign: "center",
  };

  // === Column border helper ===
  const dividerColumns = [
    "us_20_l",
    "us_amazon_20_l",
    "uk_20_l",
  ];

  const cellStyle = (bg, key) => {
    const isDivider = dividerColumns.some((col) => key.includes(col));
    return {
      background: bg,
      border: "1px solid #e5e7eb",
      borderRight: isDivider ? "3px solid #9ca3af" : "1px solid #e5e7eb",
      padding: "0.3rem",
      textAlign: "center",
      whiteSpace: "nowrap",
    };
  };


  const toggleSection = (section) => {
    setCollapsedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.3)",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          width: "98vw",
          height: "94vh",
          background: "white",
          borderRadius: 10,
          display: "flex",
          flexDirection: "column",
          border: "1px solid #e5e7eb",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "0.7rem 1rem",
            borderBottom: "1px solid #e5e7eb",
            background: "#f1f5f9",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <strong>📋 Planning Simulation - Full 32 Column View</strong>{" "}
            <span style={{ color: "#6b7280", fontSize: 12 }}>
              (Dynamic Amazon splits: US ~75%, UK ~15%)
            </span>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={reset} style={btnLight}>
              <Undo2 size={14} /> Reset
            </button>
            <button onClick={handleSaveVersion} style={btnLight}>
              💾 Save Version
            </button>
            <button onClick={handleLoadVersion} style={btnLight}>
              📂 Load Version
            </button>
            <button onClick={onClose} style={btnLight}>
              <X size={14} /> Close
            </button>
          </div>
        </div>

        {/* Table */}
        <div style={{ ...tableContainer, paddingTop: "75px" }}>


{/* 🧭 Compact Floating Horizontal Stock Summary */}
<div
  style={{
    position: "fixed",
    top: 65,
    left: 0,
    right: 0,
    zIndex: 500,
    width: "92vw",
    margin: "0 auto",
    background: "rgba(255,255,255,0.97)",
    backdropFilter: "blur(4px)",
    borderBottom: "1px solid #e5e7eb",
    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
    padding: "4px 8px",
    display: "flex",
    justifyContent: "space-around",
    alignItems: "center",
    gap: "6px",
    overflowX: "auto",
    fontSize: "0.7rem",
    whiteSpace: "nowrap",
    height: "90px",
    borderRadius: "6px",
  }}
>

  {[
    { title: "🇺🇸 ITB", color: "#dbeafe", type: "regular", region: "us" },
    { title: "🇺🇸 Amazon", color: "#fef9c3", type: "amazon", region: "us" },
    { title: "🇬🇧 TIVE", color: "#dcfce7", type: "regular", region: "uk" },
    { title: "🇬🇧 Amazon", color: "#fde68a", type: "amazon", region: "uk" },
  ].map((section) => (
    <div
      key={section.title}
      style={{
        background: section.color,
        borderRadius: 6,
        padding: "4px 8px",
        minWidth: "170px",
        flexShrink: 0,
      }}
    >
      <div
        style={{
          fontWeight: 700,
          fontSize: "0.8rem",
          marginBottom: 2,
          textAlign: "center",
          lineHeight: 1.1,
        }}
      >
        {section.title}
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr auto",
          rowGap: "1px",
        }}
      >
        {["cityplay_m", "cityplay_l", "20_m", "20_l"].map((sku) => {
          const data =
            section.type === "amazon"
              ? depletionInfo.amazon?.[`${section.region}_${sku}`] ||
                depletionInfo.amazon?.[sku] ||
                depletionInfo.amazon?.[`${section.region}.amazon.${sku}`]
              : depletionInfo.regular?.[`${section.region}_${sku}`];


          const label = sku
            .replace("cityplay", "CP")
            .replace("20_", "2.0 ")
            .replace("_m", " M")
            .replace("_l", " L")
            .toUpperCase();

          return (
            <React.Fragment key={`${section.region}_${section.type}_${sku}`}>
              <span>{label}</span>
              <span
                style={{
                  color: data?.outDate ? "#dc2626" : "#16a34a",
                  fontWeight: 600,
                }}
              >
                {data?.outDate
                  ? `Out ${new Date(data.outDate).toLocaleDateString("en-GB", {
                      day: "2-digit",
                      month: "short",
                    })}`
                  : `${data?.current ?? 0}`}
              </span>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  ))}
</div>

          <table
            style={{
              width: "100%",
              borderCollapse: "separate",
              borderSpacing: 0,
            }}
          >
            <thead>
            {/* Main Headers */}
            <tr
              style={{
                background: "#f1f5f9",
                position: "sticky",
                top: 0,
                zIndex: 40,
                height: "32px",
              }}
            >
              <th style={{ ...thSticky, left: 0, zIndex: 50 }}>📅 Date</th>
              <th colSpan={17} style={{ ...colBg("#ecfdf5"), position: "sticky", top: 0, zIndex: 40, background: "#ecfdf5", height: "32px" }}>
                📈 Sales Forecast (16 fields + target)
              </th>
              <th colSpan={17} style={{ ...colBg("#fffbeb"), position: "sticky", top: 0, zIndex: 40, background: "#fffbeb", height: "32px" }}>
                🏭 Production Plan (16 fields + target)
              </th>
              <th colSpan={17} style={{ ...colBg("#eef2ff"), position: "sticky", top: 0, zIndex: 40, background: "#eef2ff", height: "32px" }}>
                📦 Stock (Dynamic arrival with Amazon splits)
              </th>
            </tr>

              {/* Sub Headers */}
              <tr
                style={{
                  background: "#e2e8f0",
                  position: "sticky",
                  top: "30px",
                  zIndex: 35,
                  height: "26px",
                }}
              >
                <th style={thSticky}></th>

                {/* 🧾 SALES SUB-HEADERS */}
                {!collapsedSections.sales && (
                  <>
                    <th style={colBg("#ecfdf5")}>🎯 Target</th>
                    {[
                      "🇺🇸 ITB CityPlay M",
                      "🇺🇸 ITB CityPlay L",
                      "🇺🇸 ITB 2.0 M",
                      "🇺🇸 ITB 2.0 L",
                      "🇺🇸 AMZ CityPlay M",
                      "🇺🇸 AMZ CityPlay L",
                      "🇺🇸 AMZ 2.0 M",
                      "🇺🇸 AMZ 2.0 L",
                      "🇬🇧 TIVE CityPlay M",
                      "🇬🇧 TIVE CityPlay L",
                      "🇬🇧 TIVE 2.0 M",
                      "🇬🇧 TIVE 2.0 L",
                      "🇬🇧 AMZ CityPlay M",
                      "🇬🇧 AMZ CityPlay L",
                      "🇬🇧 AMZ 2.0 M",
                      "🇬🇧 AMZ 2.0 L",
                    ].map((title, idx) => {
                      const isCityPlay = title.includes("CityPlay");
                      const isTwoPointO = title.includes("2.0");
                      const bgColor = isCityPlay ? "#dbeafe" : isTwoPointO ? "#fce7f3" : "#ecfdf5";

                      const isDivider =
                        title.includes("ITB 2.0 L") ||
                        title.includes("AMZ 2.0 L") ||
                        title.includes("TIVE 2.0 L");

                      const style = {
                        ...colBg(bgColor),
                        fontWeight: 600,
                        color: "#111827",
                        borderRight: isDivider ? "3px solid #9ca3af" : "1px solid #e5e7eb",
                      };

                      return (
                        <th key={`sales-${idx}`} style={style}>
                          {title}
                        </th>
                      );
                    })}
                  </>
                )}
                {collapsedSections.sales && (
                  <th colSpan={17} style={colBg("#ecfdf5")}>...</th>
)}

                {/* 🏭 PRODUCTION SUB-HEADERS */}
                {!collapsedSections.production && (
                  <>
                    <th style={colBg("#fffbeb")}>🎯 Target</th>
                    {[
                      "🇺🇸 ITB CityPlay M",
                      "🇺🇸 ITB CityPlay L",
                      "🇺🇸 ITB 2.0 M",
                      "🇺🇸 ITB 2.0 L",
                      "🇺🇸 AMZ CityPlay M",
                      "🇺🇸 AMZ CityPlay L",
                      "🇺🇸 AMZ 2.0 M",
                      "🇺🇸 AMZ 2.0 L",
                      "🇬🇧 TIVE CityPlay M",
                      "🇬🇧 TIVE CityPlay L",
                      "🇬🇧 TIVE 2.0 M",
                      "🇬🇧 TIVE 2.0 L",
                      "🇬🇧 AMZ CityPlay M",
                      "🇬🇧 AMZ CityPlay L",
                      "🇬🇧 AMZ 2.0 M",
                      "🇬🇧 AMZ 2.0 L",
                    ].map((title, idx) => {
                      const isCityPlay = title.includes("CityPlay");
                      const isTwoPointO = title.includes("2.0");
                      const bgColor = isCityPlay ? "#dbeafe" : isTwoPointO ? "#fce7f3" : "#fffbeb";

                      const isDivider =
                        title.includes("ITB 2.0 L") ||
                        title.includes("AMZ 2.0 L") ||
                        title.includes("TIVE 2.0 L");

                      const style = {
                        ...colBg(bgColor),
                        fontWeight: 600,
                        color: "#111827",
                        borderRight: isDivider ? "3px solid #9ca3af" : "1px solid #e5e7eb",
                      };

                      return (
                        <th key={`prod-${idx}`} style={style}>
                          {title}
                        </th>
                      );
                    })}
                  </>
                )}
                {collapsedSections.production && (
                  <th colSpan={17} style={colBg("#fffbeb")}>...</th>
                )}


              {/* 📦 STOCK Sub-headers */}
              {!collapsedSections.stock && (
                <>
                  {[
                    // 🇺🇸 ITB
                    "🇺🇸 ITB CityPlay M",
                    "🇺🇸 ITB CityPlay L",
                    "🇺🇸 ITB 2.0 M",
                    "🇺🇸 ITB 2.0 L",

                    // 🇺🇸 Amazon
                    "🇺🇸 AMZ CityPlay M",
                    "🇺🇸 AMZ CityPlay L",
                    "🇺🇸 AMZ 2.0 M",
                    "🇺🇸 AMZ 2.0 L",

                    // 🇬🇧 TIVE
                    "🇬🇧 TIVE CityPlay M",
                    "🇬🇧 TIVE CityPlay L",
                    "🇬🇧 TIVE 2.0 M",
                    "🇬🇧 TIVE 2.0 L",

                    // 🇬🇧 Amazon
                    "🇬🇧 AMZ CityPlay M",
                    "🇬🇧 AMZ CityPlay L",
                    "🇬🇧 AMZ 2.0 M",
                    "🇬🇧 AMZ 2.0 L",
                  ].map((title, idx) => {
                    const isCityPlay = title.includes("CityPlay");
                    const isTwoPointO = title.includes("2.0");
                    const bgColor = isCityPlay ? "#dbeafe" : isTwoPointO ? "#fce7f3" : "#eef2ff";

                    const isDivider =
                      title.includes("ITB 2.0 L") ||
                      title.includes("AMZ 2.0 L") ||
                      title.includes("TIVE 2.0 L");

                    const style = {
                      ...colBg(bgColor),
                      fontWeight: 600,
                      color: "#111827",
                      borderRight: isDivider ? "3px solid #9ca3af" : "1px solid #e5e7eb",
                    };

                    return (
                      <th key={`stock-${idx}`} style={style}>
                        {title}
                      </th>
                    );
                  })}
                  <th style={colBg("#dcfce7")}>📦 Total</th>
                </>
              )}
              {collapsedSections.stock && (
                <th colSpan={17} style={colBg("#eef2ff")}>...</th>
              )}

              </tr>

            </thead>

            <tbody>
              {rows.map((day, idx) => {
                const isOdd = idx % 2 !== 0;
                return (
                  <tr key={day.date} style={{ background: isOdd ? "#fafafa" : "white" }}>
                    {/* Date */}
                    <td style={{ ...colBg(isOdd ? "#fafafa" : "white"), position: "sticky", left: 0, zIndex: 10, textAlign: "left" }}>
                      {new Date(day.date).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                      })}
                    </td>

                    {/* Sales Forecast */}
                    {!collapsedSections.sales && (
                      <>
                        <td style={colBg("#ecfdf5")}>
                          <input
                            type="number"
                            value={day.sales?.target_total || 0}
                            onChange={(e) => handleChangeTarget(idx, e.target.value, 'sales')}
                            style={inputCell}
                          />
                        </td>
                        {/* US ITB Sales */}
                        <td style={colBg("#ecfdf5")}>
                          <input
                            type="number"
                            value={day.forecast?.sales?.us_cityplay_m || 0}
                            onChange={(e) => handleChangeSalesField(idx, "us_cityplay_m", e.target.value)}
                            style={inputCell}
                          />
                        </td>
                        <td style={colBg("#ecfdf5")}>
                          <input
                            type="number"
                            value={day.forecast?.sales?.us_cityplay_l || 0}
                            onChange={(e) => handleChangeSalesField(idx, "us_cityplay_l", e.target.value)}
                            style={inputCell}
                          />
                        </td>
                        <td style={colBg("#ecfdf5")}>
                          <input
                            type="number"
                            value={day.forecast?.sales?.us_20_m || 0}
                            onChange={(e) => handleChangeSalesField(idx, "us_20_m", e.target.value)}
                            style={inputCell}
                          />
                        </td>
                        <td style={colBg("#ecfdf5")}>
                          <input
                            type="number"
                            value={day.forecast?.sales?.us_20_l || 0}
                            onChange={(e) => handleChangeSalesField(idx, "us_20_l", e.target.value)}
                            style={inputCell}
                          />
                        </td>
                        {/* US Amazon Sales */}
                        <td style={colBg("#ecfdf5")}>
                          <input
                            type="number"
                            value={day.forecast?.sales_amazon?.us_cityplay_m || 0}
                            onChange={(e) => handleChangeSalesField(idx, "us_cityplay_m", e.target.value, true)}
                            style={inputCell}
                          />
                        </td>
                        <td style={colBg("#ecfdf5")}>
                          <input
                            type="number"
                            value={day.forecast?.sales_amazon?.us_cityplay_l || 0}
                            onChange={(e) => handleChangeSalesField(idx, "us_cityplay_l", e.target.value, true)}
                            style={inputCell}
                          />
                        </td>
                        <td style={colBg("#ecfdf5")}>
                          <input
                            type="number"
                            value={day.forecast?.sales_amazon?.us_20_m || 0}
                            onChange={(e) => handleChangeSalesField(idx, "us_20_m", e.target.value, true)}
                            style={inputCell}
                          />
                        </td>
                        <td style={colBg("#ecfdf5")}>
                          <input
                            type="number"
                            value={day.forecast?.sales_amazon?.us_20_l || 0}
                            onChange={(e) => handleChangeSalesField(idx, "us_20_l", e.target.value, true)}
                            style={inputCell}
                          />
                        </td>
                        {/* UK TIVE Sales */}
                        <td style={colBg("#ecfdf5")}>
                          <input
                            type="number"
                            value={day.forecast?.sales?.uk_cityplay_m || 0}
                            onChange={(e) => handleChangeSalesField(idx, "uk_cityplay_m", e.target.value)}
                            style={inputCell}
                          />
                        </td>
                        <td style={colBg("#ecfdf5")}>
                          <input
                            type="number"
                            value={day.forecast?.sales?.uk_cityplay_l || 0}
                            onChange={(e) => handleChangeSalesField(idx, "uk_cityplay_l", e.target.value)}
                            style={inputCell}
                          />
                        </td>
                        <td style={colBg("#ecfdf5")}>
                          <input
                            type="number"
                            value={day.forecast?.sales?.uk_20_m || 0}
                            onChange={(e) => handleChangeSalesField(idx, "uk_20_m", e.target.value)}
                            style={inputCell}
                          />
                        </td>
                        <td style={colBg("#ecfdf5")}>
                          <input
                            type="number"
                            value={day.forecast?.sales?.uk_20_l || 0}
                            onChange={(e) => handleChangeSalesField(idx, "uk_20_l", e.target.value)}
                            style={inputCell}
                          />
                        </td>
                        {/* UK Amazon Sales */}
                        <td style={colBg("#ecfdf5")}>
                          <input
                            type="number"
                            value={day.forecast?.sales_amazon?.uk_cityplay_m || 0}
                            onChange={(e) => handleChangeSalesField(idx, "uk_cityplay_m", e.target.value, true)}
                            style={inputCell}
                          />
                        </td>
                        <td style={colBg("#ecfdf5")}>
                          <input
                            type="number"
                            value={day.forecast?.sales_amazon?.uk_cityplay_l || 0}
                            onChange={(e) => handleChangeSalesField(idx, "uk_cityplay_l", e.target.value, true)}
                            style={inputCell}
                          />
                        </td>
                        <td style={colBg("#ecfdf5")}>
                          <input
                            type="number"
                            value={day.forecast?.sales_amazon?.uk_20_m || 0}
                            onChange={(e) => handleChangeSalesField(idx, "uk_20_m", e.target.value, true)}
                            style={inputCell}
                          />
                        </td>
                        <td style={colBg("#ecfdf5")}>
                          <input
                            type="number"
                            value={day.forecast?.sales_amazon?.uk_20_l || 0}
                            onChange={(e) => handleChangeSalesField(idx, "uk_20_l", e.target.value, true)}
                            style={inputCell}
                          />
                        </td>
                      </>
                    )}
                    {collapsedSections.sales && (
                      <td colSpan={17} style={colBg("#ecfdf5")}>
                        Total: {day.sales?.target_total || 0}
                      </td>
                    )}

                    {/* Production Plan */}
                    {!collapsedSections.production && (
                      <>
                        <td style={colBg("#fffbeb")}>
                          <input
                            type="number"
                            value={day.production_target_total || 0}
                            onChange={(e) => handleChangeTarget(idx, e.target.value, 'production')}
                            style={inputCell}
                          />
                        </td>

{/* 🇺🇸 US ITB Production */}
<td style={colBg("#fffbeb")}>
  <input
    key={`${day.date}-us_cityplay_m-${refreshKey}`}
    type="number"
    defaultValue={getDisplayValue(day, "us_cityplay_m", false)}
    onBlur={(e) => handleChangeProdField(idx, "us_cityplay_m", e.target.value)}
    readOnly={!!lockedCells?.[day.date]?.["us_cityplay_m"]}
    style={{
      ...inputCell,
      background: lockedCells?.[day.date]?.["us_cityplay_m"] ? "#e5e7eb" : "white",
      color: lockedCells?.[day.date]?.["us_cityplay_m"] ? "#6b7280" : "black",
    }}
  />
</td>
<td style={colBg("#fffbeb")}>
  <input
    key={`${day.date}-us_cityplay_l-${refreshKey}`}
    type="number"
    defaultValue={getDisplayValue(day, "us_cityplay_l", false)}
    onBlur={(e) => handleChangeProdField(idx, "us_cityplay_l", e.target.value)}
    readOnly={!!lockedCells?.[day.date]?.["us_cityplay_l"]}
    style={{
      ...inputCell,
      background: lockedCells?.[day.date]?.["us_cityplay_l"] ? "#e5e7eb" : "white",
      color: lockedCells?.[day.date]?.["us_cityplay_l"] ? "#6b7280" : "black",
    }}
  />
</td>
<td style={colBg("#fffbeb")}>
  <input
    key={`${day.date}-us_20_m-${refreshKey}`}
    type="number"
    defaultValue={getDisplayValue(day, "us_20_m", false)}
    onBlur={(e) => handleChangeProdField(idx, "us_20_m", e.target.value)}
    readOnly={!!lockedCells?.[day.date]?.["us_20_m"]}
    style={{
      ...inputCell,
      background: lockedCells?.[day.date]?.["us_20_m"] ? "#e5e7eb" : "white",
      color: lockedCells?.[day.date]?.["us_20_m"] ? "#6b7280" : "black",
    }}
  />
</td>
<td style={cellStyle("#fffbeb", "us_20_l")}>
  <input
    key={`${day.date}-us_20_l-${refreshKey}`}
    type="number"
    defaultValue={getDisplayValue(day, "us_20_l", false)}
    onBlur={(e) => handleChangeProdField(idx, "us_20_l", e.target.value)}
    readOnly={!!lockedCells?.[day.date]?.["us_20_l"]}
    style={{
      ...inputCell,
      background: lockedCells?.[day.date]?.["us_20_l"] ? "#e5e7eb" : "white",
      color: lockedCells?.[day.date]?.["us_20_l"] ? "#6b7280" : "black",
    }}
  />
</td>

{/* 🇺🇸 Amazon Production */}
<td style={colBg("#fffbeb")}>
  <input
    key={`${day.date}-us_cityplay_m_amz-${refreshKey}`}
    type="number"
    defaultValue={getDisplayValue(day, "us_cityplay_m", true)}
    onBlur={(e) => handleChangeProdField(idx, "us_cityplay_m", e.target.value, true)}
    readOnly={!!lockedCells?.[day.date]?.["us_cityplay_m__amz"]}
    style={{
      ...inputCell,
      background: lockedCells?.[day.date]?.["us_cityplay_m__amz"] ? "#e5e7eb" : "white",
      color: lockedCells?.[day.date]?.["us_cityplay_m__amz"] ? "#6b7280" : "black",
    }}
  />
</td>
<td style={colBg("#fffbeb")}>
  <input
    key={`${day.date}-us_cityplay_l_amz-${refreshKey}`}
    type="number"
    defaultValue={getDisplayValue(day, "us_cityplay_l", true)}
    onBlur={(e) => handleChangeProdField(idx, "us_cityplay_l", e.target.value, true)}
    readOnly={!!lockedCells?.[day.date]?.["us_cityplay_l__amz"]}
    style={{
      ...inputCell,
      background: lockedCells?.[day.date]?.["us_cityplay_l__amz"] ? "#e5e7eb" : "white",
      color: lockedCells?.[day.date]?.["us_cityplay_l__amz"] ? "#6b7280" : "black",
    }}
  />
</td>
<td style={colBg("#fffbeb")}>
  <input
    key={`${day.date}-us_20_m_amz-${refreshKey}`}
    type="number"
    defaultValue={getDisplayValue(day, "us_20_m", true)}
    onBlur={(e) => handleChangeProdField(idx, "us_20_m", e.target.value, true)}
    readOnly={!!lockedCells?.[day.date]?.["us_20_m__amz"]}
    style={{
      ...inputCell,
      background: lockedCells?.[day.date]?.["us_20_m__amz"] ? "#e5e7eb" : "white",
      color: lockedCells?.[day.date]?.["us_20_m__amz"] ? "#6b7280" : "black",
    }}
  />
</td>
<td style={cellStyle("#fffbeb", "us_amazon_20_l")}>
  <input
    key={`${day.date}-us_20_l_amz-${refreshKey}`}
    type="number"
    defaultValue={getDisplayValue(day, "us_20_l", true)}
    onBlur={(e) => handleChangeProdField(idx, "us_20_l", e.target.value, true)}
    readOnly={!!lockedCells?.[day.date]?.["us_20_l__amz"]}
    style={{
      ...inputCell,
      background: lockedCells?.[day.date]?.["us_20_l__amz"] ? "#e5e7eb" : "white",
      color: lockedCells?.[day.date]?.["us_20_l__amz"] ? "#6b7280" : "black",
    }}
  />
</td>

{/* 🇬🇧 UK TIVE Production */}
<td style={colBg("#fffbeb")}>
  <input
    key={`${day.date}-uk_cityplay_m-${refreshKey}`}
    type="number"
    defaultValue={getDisplayValue(day, "uk_cityplay_m", false)}
    onBlur={(e) => handleChangeProdField(idx, "uk_cityplay_m", e.target.value)}
    readOnly={!!lockedCells?.[day.date]?.["uk_cityplay_m"]}
    style={{
      ...inputCell,
      background: lockedCells?.[day.date]?.["uk_cityplay_m"] ? "#e5e7eb" : "white",
      color: lockedCells?.[day.date]?.["uk_cityplay_m"] ? "#6b7280" : "black",
    }}
  />
</td>
<td style={colBg("#fffbeb")}>
  <input
    key={`${day.date}-uk_cityplay_l-${refreshKey}`}
    type="number"
    defaultValue={getDisplayValue(day, "uk_cityplay_l", false)}
    onBlur={(e) => handleChangeProdField(idx, "uk_cityplay_l", e.target.value)}
    readOnly={!!lockedCells?.[day.date]?.["uk_cityplay_l"]}
    style={{
      ...inputCell,
      background: lockedCells?.[day.date]?.["uk_cityplay_l"] ? "#e5e7eb" : "white",
      color: lockedCells?.[day.date]?.["uk_cityplay_l"] ? "#6b7280" : "black",
    }}
  />
</td>
<td style={colBg("#fffbeb")}>
  <input
    key={`${day.date}-uk_20_m-${refreshKey}`}
    type="number"
    defaultValue={getDisplayValue(day, "uk_20_m", false)}
    onBlur={(e) => handleChangeProdField(idx, "uk_20_m", e.target.value)}
    readOnly={!!lockedCells?.[day.date]?.["uk_20_m"]}
    style={{
      ...inputCell,
      background: lockedCells?.[day.date]?.["uk_20_m"] ? "#e5e7eb" : "white",
      color: lockedCells?.[day.date]?.["uk_20_m"] ? "#6b7280" : "black",
    }}
  />
</td>
<td style={cellStyle("#fffbeb", "uk_20_l")}>
  <input
    key={`${day.date}-uk_20_l-${refreshKey}`}
    type="number"
    defaultValue={getDisplayValue(day, "uk_20_l", false)}
    onBlur={(e) => handleChangeProdField(idx, "uk_20_l", e.target.value)}
    readOnly={!!lockedCells?.[day.date]?.["uk_20_l"]}
    style={{
      ...inputCell,
      background: lockedCells?.[day.date]?.["uk_20_l"] ? "#e5e7eb" : "white",
      color: lockedCells?.[day.date]?.["uk_20_l"] ? "#6b7280" : "black",
    }}
  />
</td>

{/* 🇬🇧 Amazon Production */}
<td style={colBg("#fffbeb")}>
  <input
    key={`${day.date}-uk_cityplay_m_amz-${refreshKey}`}
    type="number"
    defaultValue={getDisplayValue(day, "uk_cityplay_m", true)}
    onBlur={(e) => handleChangeProdField(idx, "uk_cityplay_m", e.target.value, true)}
    readOnly={!!lockedCells?.[day.date]?.["uk_cityplay_m__amz"]}
    style={{
      ...inputCell,
      background: lockedCells?.[day.date]?.["uk_cityplay_m__amz"] ? "#e5e7eb" : "white",
      color: lockedCells?.[day.date]?.["uk_cityplay_m__amz"] ? "#6b7280" : "black",
    }}
  />
</td>
<td style={colBg("#fffbeb")}>
  <input
    key={`${day.date}-uk_cityplay_l_amz-${refreshKey}`}
    type="number"
    defaultValue={getDisplayValue(day, "uk_cityplay_l", true)}
    onBlur={(e) => handleChangeProdField(idx, "uk_cityplay_l", e.target.value, true)}
    readOnly={!!lockedCells?.[day.date]?.["uk_cityplay_l__amz"]}
    style={{
      ...inputCell,
      background: lockedCells?.[day.date]?.["uk_cityplay_l__amz"] ? "#e5e7eb" : "white",
      color: lockedCells?.[day.date]?.["uk_cityplay_l__amz"] ? "#6b7280" : "black",
    }}
  />
</td>
<td style={colBg("#fffbeb")}>
  <input
    key={`${day.date}-uk_20_m_amz-${refreshKey}`}
    type="number"
    defaultValue={getDisplayValue(day, "uk_20_m", true)}
    onBlur={(e) => handleChangeProdField(idx, "uk_20_m", e.target.value, true)}
    readOnly={!!lockedCells?.[day.date]?.["uk_20_m__amz"]}
    style={{
      ...inputCell,
      background: lockedCells?.[day.date]?.["uk_20_m__amz"] ? "#e5e7eb" : "white",
      color: lockedCells?.[day.date]?.["uk_20_m__amz"] ? "#6b7280" : "black",
    }}
  />
</td>
<td style={colBg("#fffbeb")}>
  <input
    key={`${day.date}-uk_20_l_amz-${refreshKey}`}
    type="number"
    defaultValue={getDisplayValue(day, "uk_20_l", true)}
    onBlur={(e) => handleChangeProdField(idx, "uk_20_l", e.target.value, true)}
    readOnly={!!lockedCells?.[day.date]?.["uk_20_l__amz"]}
    style={{
      ...inputCell,
      background: lockedCells?.[day.date]?.["uk_20_l__amz"] ? "#e5e7eb" : "white",
      color: lockedCells?.[day.date]?.["uk_20_l__amz"] ? "#6b7280" : "black",
    }}
  />
</td>


                      </>
                    )}
                    {collapsedSections.production && (
                      <td colSpan={17} style={colBg("#fffbeb")}>
                        Total: {day.production_target_total || 0}
                      </td>
                    )}


                    {/* 📦 STOCK (Dynamic arrival with Amazon splits) */}
                    {!collapsedSections.stock && (
                      <>
                        {/* 🇺🇸 ITB */}
                        <td style={colBg("#eef2ff")}>{day.stock?.us_cityplay_m ?? "-"}</td>
                        <td style={colBg("#eef2ff")}>{day.stock?.us_cityplay_l ?? "-"}</td>
                        <td style={colBg("#eef2ff")}>{day.stock?.us_20_m ?? "-"}</td>
                        <td style={colBg("#eef2ff")}>{day.stock?.us_20_l ?? "-"}</td>

                        {/* 🇺🇸 Amazon */}
                        <td style={colBg("#eef2ff")}>{day.stock?.amazon?.us_cityplay_m ?? "-"}</td>
                        <td style={colBg("#eef2ff")}>{day.stock?.amazon?.us_cityplay_l ?? "-"}</td>
                        <td style={colBg("#eef2ff")}>{day.stock?.amazon?.us_20_m ?? "-"}</td>
                        <td style={colBg("#eef2ff")}>{day.stock?.amazon?.us_20_l ?? "-"}</td>

                        {/* 🇬🇧 TIVE */}
                        <td style={colBg("#eef2ff")}>{day.stock?.uk_cityplay_m ?? "-"}</td>
                        <td style={colBg("#eef2ff")}>{day.stock?.uk_cityplay_l ?? "-"}</td>
                        <td style={colBg("#eef2ff")}>{day.stock?.uk_20_m ?? "-"}</td>
                        <td style={colBg("#eef2ff")}>{day.stock?.uk_20_l ?? "-"}</td>

                        {/* 🇬🇧 Amazon */}
                        <td style={colBg("#eef2ff")}>{day.stock?.amazon?.uk_cityplay_m ?? "-"}</td>
                        <td style={colBg("#eef2ff")}>{day.stock?.amazon?.uk_cityplay_l ?? "-"}</td>
                        <td style={colBg("#eef2ff")}>{day.stock?.amazon?.uk_20_m ?? "-"}</td>
                        <td style={colBg("#eef2ff")}>{day.stock?.amazon?.uk_20_l ?? "-"}</td>

                        {/* 📦 TOTAL */}
                        <td style={colBg("#dcfce7")}>{day.stock?.total_stock ?? "-"}</td>
                      </>
                    )}
                    {collapsedSections.stock && (
                      <td colSpan={17} style={colBg("#eef2ff")}>
                        Total Stock: {day.stock?.total_stock ?? "-"}
                      </td>
                    )}

                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div
          style={{
            padding: "0.4rem 1rem",
            borderTop: "1px solid #e5e7eb",
            background: "white",
            display: "flex",
            justifyContent: "space-between",
            fontSize: 12,
          }}
        >
          <span>{isDirty ? "Unsaved changes in simulation" : "No changes"}</span>
          <span style={{ color: "#6b7280" }}>
            * Using dynamic Amazon splits: US ~75%, UK ~15% (varies by SKU)
          </span>
        </div>
      </div>
    </div>
  );
}

// Button styles
const btnLight = {
  background: "white",
  border: "1px solid #e5e7eb",
  borderRadius: 6,
  padding: "4px 10px",
  display: "flex",
  gap: 4,
  alignItems: "center",
  cursor: "pointer",
  fontSize: 13,
};

const btnApply = {
  background: "#10b981",
  color: "white",
  border: "none",
  borderRadius: 6,
  padding: "4px 10px",
  display: "flex",
  gap: 4,
  alignItems: "center",
  cursor: "pointer",
  fontSize: 13,
};